// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xIsAuthenticated } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { css } from "styled-components";
import { fontConfig } from "@vanmeter/XDL/constants";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import Link, { LinkPresentationProps } from "@vanmeter/MobiusOverrides/Link/Link";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";

export interface CompanyInformationStyles {
    wrapper?: InjectableCss;
    companyName?: TypographyPresentationProps;
    addressInfo?: AddressInfoCondensedDisplayStyles;
    phoneLink?: LinkPresentationProps;
}

export const companyInformationStyles: CompanyInformationStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    companyName: { ...fontConfig.xSmallSingleLine, weight: fontConfig.weight.bold, color: "common.white" },
    addressInfo: {
        addressText: { ...fontConfig.xSmallSingleLine, color: "common.white" },
        cityStatePostalCodeText: { ...fontConfig.xSmallSingleLine, color: "common.white" },
    },
    phoneLink: {
        variant: "secondary" as any,
        typographyProps: {
            ...fontConfig.xSmallSingleLine,
        },
    },
};

const styles = companyInformationStyles;

type Props = WidgetModule & WidgetProps;

const CompanyInformation: FC<Props> = ({ id }) => {
    const { website, contactPhoneNumber } = useAppSelector(state => ({
        website: state.context.website,
        contactPhoneNumber: (getSettingsCollection(state).customSettings as any).contactPhoneNumber,
    }));

    return (
        <StyledWrapper {...styles.wrapper}>
            {website.name && <Typography {...styles.companyName}>{website.name}</Typography>}
            <AddressInfoCondensedDisplay
                address1={website.address1}
                address2={website.address2}
                city={website.city}
                state={website.state}
                postalCode={website.postalCode}
                extendedStyles={styles.addressInfo}
            />
            {contactPhoneNumber && (
                <Link href={`tel:${contactPhoneNumber}`} {...styles.phoneLink}>
                    {contactPhoneNumber}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CompanyInformation,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        displayName: "Company Information",
        fieldDefinitions: [
            {
                name: "This widget displays company information. The content is automatically retrieved from website information, while the phone number is sourced from custom settings.",
                editorTemplate: "LabelField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
